import { AccountType } from '@/data/usecases/account/account-type'
import { LabelValueOptionModel } from '@/domain/models/onboarding/label-value-option-model'
import { Account } from '@/domain/usecases'
import { AccountBenefit } from '@/domain/usecases/account/account-benefit'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { queryClient } from '@/infra/http'
import { importRemote } from '@/main/routes/helpers/loaders'

export type UserProfileLoaderBenefitOptionsType = {
  accountBenefits: AccountBenefit.Model
  benefitsOptions: AccountBenefit.OptionsModel
}

export type UserProfileLoaderProfessionalOptionsType = {
  areas: LabelValueOptionModel<number>[]
  seniorities: LabelValueOptionModel<number>[]
}

export type UserProfileLoaderDataType = {
  accountProfile: Account.ProfileModel
  accountProfessional?: AccountProfessional.Model
  accountBenefits: AccountBenefit.Model
  professionalOptions?: UserProfileLoaderProfessionalOptionsType
  benefitsOptions: AccountBenefit.OptionsModel
}

/**
 * Loads user profile data including account profile, professional data, benefits, and options
 * Handles both dependent and regular accounts with different data requirements
 */
export async function loadUserProfile(account: Account.Model): Promise<UserProfileLoaderDataType> {
  const isDependent = AccountType.Enum.DEPENDENT.is(account)

  // Get account profile (always required)
  const remoteAccountProfile = await importRemote<Account>('makeRemoteAccountProfile', queryClient)
  const accountProfile = await remoteAccountProfile.loader()

  // Get benefits (always required)
  const benefits = await remoteAccountProfile.getBenefits(account.id)
  const accountBenefits = benefits
  const benefitsOptions: AccountBenefit.OptionsModel = {
    passPlans: benefits.contractedPlan.plan ? [benefits.contractedPlan.plan] : []
  }

  // Get professional data and options (only for non-dependent accounts)
  let accountProfessional: AccountProfessional.Model | undefined
  let professionalOptions: UserProfileLoaderProfessionalOptionsType | undefined

  if (!isDependent) {
    const [remoteAccountProfessional, remoteOnboardingOptions] = await Promise.all([
      importRemote<AccountProfessional>('makeRemoteAccountProfessional', queryClient),
      importRemote<OnboardingOptions>('makeRemoteOnboardingOptions', queryClient)
    ])

    const [professional, step1Options] = await Promise.all([
      remoteAccountProfessional.get(account.id),
      remoteOnboardingOptions.getStep1()
    ])

    accountProfessional = professional
    professionalOptions = {
      areas: step1Options.professionalAreas,
      seniorities: step1Options.professionalSeniorities
    }
  }

  return {
    accountProfile,
    accountProfessional,
    accountBenefits,
    benefitsOptions,
    professionalOptions
  }
}
